import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  ChevronDown, 
  User, 
  X, 
  Check, 
  Loader2, 
  UserCircle2, 
  Package, 
  LogOut,
  Search,
  Building2
} from 'lucide-react';
import { api } from "../services/api";
import AuthModal from "./AuthModal";

const LOGO_CACHE_KEY = "website_logo";
const CITY_STORAGE_KEY = "user_selected_city";
const CITY_FULL_DATA_KEY = "selected_city_full_data";

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const [logo, setLogo] = useState<string | null>(null);
  const [showCityModal, setShowCityModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [cities, setCities] = useState<any[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [currentCity, setCurrentCity] = useState("Select City");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [citySearch, setCitySearch] = useState("");

  // --- EXISTING LOGIC (Unchanged) ---
  useEffect(() => {
    const token = localStorage.getItem("user_token");
    setIsLoggedIn(!!token);

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    
    const cachedLogo = localStorage.getItem(LOGO_CACHE_KEY);
    if (cachedLogo) setLogo(cachedLogo);
    
    const savedCity = localStorage.getItem(CITY_STORAGE_KEY);
    if (savedCity) setCurrentCity(savedCity);

    api.get("/website/logo").then((res: any) => {
      if (res?.logo) { 
        setLogo(res.logo); 
        localStorage.setItem(LOGO_CACHE_KEY, res.logo); 
      }
    }).catch(() => {});

    setLoadingCities(true);
    api.get("/cities").then((res: any) => {
      const cityList = Array.isArray(res) ? res : (res.data || []);
      setCities(cityList);
    }).finally(() => setLoadingCities(false));

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCitySelect = (city: any) => {
    setCurrentCity(city.name);
    localStorage.setItem(CITY_STORAGE_KEY, city.name);
    localStorage.setItem(CITY_FULL_DATA_KEY, JSON.stringify(city));
    window.dispatchEvent(new Event("cityUpdated"));
    setShowCityModal(false);
    setCitySearch("");
  };

  const handleLogout = () => {
    localStorage.removeItem("user_token");
    setIsLoggedIn(false);
    setShowProfileDropdown(false);
    window.location.href = "/";
  };

  const filteredCities = cities.filter(city => 
    city.name.toLowerCase().includes(citySearch.toLowerCase())
  );

  return (
    <>
      {/* 1. PREMIUM NAVBAR CONTAINER */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100 shadow-[0_4px_30px_rgb(0,0,0,0.03)] transition-all duration-300">
        {/* Increased height to h-20 for a more breathable, premium look */}
        <div className="max-w-[1300px] mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
          
          <div className="flex items-center gap-10">
            {/* 2. ENLARGED LOGO */}
            <div className="flex-shrink-0 cursor-pointer group" onClick={() => navigate('/')}>
              {logo ? (
                <img 
                  src={logo} 
                  alt="Sell30" 
                  // Increased height significantly (h-10 to h-12)
                  className="h-10 md:h-12 w-auto object-contain transform group-hover:scale-105 transition-transform duration-300" 
                />
              ) : (
                <div className="flex items-center tracking-tighter font-black text-2xl text-slate-900 group-hover:scale-105 transition-transform duration-300">
                  <span>Sell</span><span className="text-[#363C90]">30</span>
                </div>
              )}
            </div>

            {/* 3. SLEEK NAV LINKS */}
            <ul className="hidden lg:flex items-center gap-2">
              {['Sell Phone', 'About Us'].map((item) => (
                <li key={item} 
                  onClick={() => {
                    if(item === 'Sell Phone') navigate('/sell/brands');
                    if(item === 'About Us') navigate('/about');
                  }}
                  className="text-[14px] font-semibold text-slate-600 hover:text-[#363C90] px-5 py-2.5 rounded-full hover:bg-indigo-50/50 cursor-pointer transition-all duration-300">
                {item}
              </li>
              ))}
            </ul>
          </div>

          {/* 4. PREMIUM ACTIONS AREA */}
          <div className="flex items-center gap-4">
            
            {/* City Picker (Pill Shaped) */}
            <div 
              onClick={() => setShowCityModal(true)} 
              className="flex items-center gap-2 cursor-pointer group bg-slate-50 px-4 py-2.5 rounded-full border border-slate-200 hover:border-[#21C2A5]/50 hover:bg-white hover:shadow-sm transition-all duration-300"
            >
              <MapPin size={16} className="text-[#363C90] group-hover:text-[#21C2A5] transition-colors" />
              <span className="text-[13px] font-bold text-slate-700">{currentCity}</span>
              <ChevronDown size={14} className="text-slate-400 group-hover:text-[#363C90] transition-colors" />
            </div>

            <div className="w-[1px] h-6 bg-slate-200 mx-1 hidden sm:block" />

            {!isLoggedIn ? (
              <button 
                onClick={() => setShowAuthModal(true)}
                className="bg-gradient-to-r from-[#1E1B4B] to-[#363C90] text-white px-7 py-2.5 rounded-full text-[13px] font-bold shadow-lg shadow-indigo-900/20 hover:shadow-indigo-900/40 hover:-translate-y-0.5 transition-all duration-300 active:scale-95"
              >
                Login
              </button>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-[#363C90] rounded-full text-[13px] font-bold border border-indigo-100 hover:bg-indigo-100 hover:shadow-sm transition-all duration-300"
                >
                  <UserCircle2 size={18} />
                  <span className="hidden sm:inline">Account</span>
                  <ChevronDown size={14} className={`transition-transform duration-300 ${showProfileDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showProfileDropdown && (
                  <div className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <button onClick={() => {navigate('/profile'); setShowProfileDropdown(false)}} className="w-full flex items-center gap-3 px-5 py-3 text-slate-600 hover:bg-slate-50 hover:text-[#363C90] transition-colors">
                      <User size={18} />
                      <span className="text-[13px] font-bold">My Profile</span>
                    </button>
                    <button onClick={() => {navigate('/orders'); setShowProfileDropdown(false)}} className="w-full flex items-center gap-3 px-5 py-3 text-slate-600 hover:bg-slate-50 hover:text-[#363C90] transition-colors">
                      <Package size={18} />
                      <span className="text-[13px] font-bold">My Orders</span>
                    </button>
                    <div className="h-[1px] bg-slate-100 my-2 mx-4" />
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-5 py-3 text-red-500 hover:bg-red-50 transition-colors">
                      <LogOut size={18} />
                      <span className="text-[13px] font-bold">Logout</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* --- CITY MODAL (Unchanged Logic, Slight visual tweaks for consistency) --- */}
      {showCityModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-[440px] rounded-[24px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100">
            <div className="p-6 pb-4 flex items-center justify-between border-b border-slate-50">
              <h3 className="text-xl font-black text-slate-900">Select City</h3>
              <button onClick={() => setShowCityModal(false)} className="p-2 bg-slate-50 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-all">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input 
                  type="text"
                  placeholder="Search city..."
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:bg-white focus:border-[#363C90]/40 focus:ring-4 focus:ring-[#363C90]/5 transition-all font-bold text-sm text-slate-700"
                />
              </div>

              <div className="h-[320px] overflow-y-auto pr-2 custom-scrollbar">
                {loadingCities ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
                    <Loader2 className="animate-spin text-[#363C90]" size={28} />
                    <span className="text-xs font-bold uppercase tracking-widest">Loading...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {filteredCities.map((city) => (
                      <button
                        key={city.id}
                        onClick={() => handleCitySelect(city)}
                        className={`flex items-center justify-between px-5 py-3.5 rounded-2xl transition-all group ${
                          currentCity === city.name 
                          ? 'bg-indigo-50/80 text-[#363C90] border border-indigo-100' 
                          : 'hover:bg-slate-50 text-slate-600 border border-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Building2 size={16} className={currentCity === city.name ? 'text-[#363C90]' : 'text-slate-400'} />
                          <span className="text-[14px] font-bold">{city.name}</span>
                        </div>
                        {currentCity === city.name && <Check size={16} className="text-[#363C90]" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        onSuccess={() => setIsLoggedIn(true)} 
      />
    </>
  );
};

export default Header;